import { FetchResult, gql, MutationHookOptions, MutationResult, useApolloClient, useMutation } from '@apollo/client';
import {
  ForecastPageLimitsResponse,
  forecastTemplateLimitsQuery,
  inputFieldLimitsQuery,
} from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, Measure } from '@e2m-customer-portal/shared';
import { Interval, toDate } from 'date-fns';
import { useAsync } from 'react-use';
import {
  DefaultForecastLimitsQuery,
  Resolution,
  SetTemplatedForecastDataMutation,
  SetTemplatedForecastDataMutationVariables,
  Weekday,
} from '../../../generated/graphql';
import { getMonths } from '../../../utils';

export function useLimits(
  marketLocationId: string,
  deviceId: string | undefined,
  interval: Interval,
  onComplete?: (template: DefaultForecastLimitsQuery['getDefaultForecast'], fields: ForecastPageLimitsResponse) => void,
): {
  data: { template: DefaultForecastLimitsQuery['getDefaultForecast']; fields: ForecastPageLimitsResponse } | undefined;
  loading: boolean;
} {
  const client = useApolloClient();

  const { value, loading } = useAsync(async () => {
    if (deviceId == null) {
      return;
    }

    const [template, fields] = await Promise.all([
      client.query({
        query: forecastTemplateLimitsQuery,
        variables: {
          deviceId,
        },
      }),
      client.query({
        query: inputFieldLimitsQuery,
        variables: {
          device: {
            marketLocationId,
            deviceId,
          },
          months: getMonths(interval),
        },
      }),
    ]);

    if (onComplete) {
      onComplete(template.data.getDefaultForecast, fields.data.getForecastLimits);
    }

    return {
      template: template.data.getDefaultForecast,
      fields: fields.data.getForecastLimits,
    };
  }, [marketLocationId, deviceId, ...getMonths(interval), onComplete]);

  return {
    data: value,
    loading,
  };
}

export function useTemplatedForecastDataMutation(
  options?: MutationHookOptions<SetTemplatedForecastDataMutation, SetTemplatedForecastDataMutationVariables>,
): [
  (
    device: string,
    interval: Interval,
    resolution: Resolution,
    weekdays: Weekday[],
    hours: number[],
    values: readonly { type: InputFieldType; value: Measure }[],
    returnInterval?: Interval,
    opts?: MutationHookOptions<SetTemplatedForecastDataMutation, SetTemplatedForecastDataMutationVariables>,
  ) => Promise<FetchResult<SetTemplatedForecastDataMutation>>,
  MutationResult<SetTemplatedForecastDataMutation>,
] {
  const [update, data] = useMutation<SetTemplatedForecastDataMutation, SetTemplatedForecastDataMutationVariables>(
    gql`
      mutation SetTemplatedForecastData(
        $device: ID!
        $interval: IntervalInput!
        $resolution: Resolution!
        $weekdays: [Weekday!]!
        $hours: [Int!]!
        $values: [FieldValueInput!]!
        $returnInterval: IntervalInput
      ) {
        updateForecastWithTemplate(
          device: $device
          interval: $interval
          resolution: $resolution
          weekdays: $weekdays
          hours: $hours
          values: $values
          returnInterval: $returnInterval
        ) {
          id
          date
          hasDowntime
          fields {
            type
            watt
          }
        }
      }
    `,
    options,
  );

  return [
    (device, interval, resolution, weekdays, hours, values, returnInterval, opts) =>
      update({
        ...opts,
        variables: {
          device,
          interval: {
            start: toDate(interval.start),
            end: toDate(interval.end),
          },
          resolution,
          weekdays,
          hours,
          values: values.map((field) => ({
            type: field.type,
            watt: convertMeasure(field.value, 'W').value,
          })),
          returnInterval: returnInterval
            ? {
                start: toDate(returnInterval.start),
                end: toDate(returnInterval.end),
              }
            : undefined,
        },
      }),
    data,
  ];
}
